/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
/**
 * Sort direction for a sort criteria (order by)
 */
import {AfterViewInit, Component, ContentChild, ContentChildren, EventEmitter, Input, Output, QueryList} from '@angular/core';
import {merge, Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {FiqlService, SearchQuery} from '../../fiql/fiql.service';
import {isBlank} from '../../utils/lang';

import {TableColumnFilters} from './table-filters.directive';
import {TableColumnHeader} from './table-header.component';
import {SortDirection, SortState} from './table.constants';
import {SortExpression, SortKey} from './table.interfaces';

@Component({
  selector: 'gp-table',
  template: `
    <div class="table-responsive">
      <table class="table table-striped gp-table" [class.gp-table-fixed]="fixedLayout">
        <ng-content></ng-content>
      </table>
    </div>
  `,
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements AfterViewInit {

  @Input()
  public fixedLayout = true;

  private sortKey: SortKey = [];

  @Output()
  private sortChange: EventEmitter<SortKey> = new EventEmitter<SortKey>();

  @Output()
  private filtersChange: EventEmitter<SearchQuery> = new EventEmitter<SearchQuery>();

  @Output()
  private queryChange: Observable<string> = merge(this.sortChange, this.filtersChange)
    .pipe(
      map(() => {
        const search = this.fiql.serialize(this.filtersColumn.searchQuery);
        const sort = this.sortKey.map(element => {
          const prefix = element.direction === SortDirection.DESCENDING ? '-' : '';
          return `${prefix}${element.key}`;
        }).join(',');
        return `search=${search}&sort=${sort}`;
      })
    );

  @ContentChildren(TableColumnHeader, {descendants: true})
  private headers: QueryList<TableColumnHeader>;

  // TODO to verify static
  @ContentChild(TableColumnFilters)
  private filtersColumn: TableColumnFilters;

  constructor(private fiql: FiqlService) {
  }

  public ngAfterViewInit() {
    // Listen to sort events on all sort columns of this table
    merge(...this.headers.map(header => header.sortEvents)).subscribe(this.sortKeyChanged.bind(this));

    // Check for default sort
    setTimeout(() => {
      const sortColumnsWithDefaultSort =
        this.headers.filter((header: TableColumnHeader) => !isBlank(header.defaultSort));
      if (sortColumnsWithDefaultSort.length > 1) {
        console.warn('Multipe "default-sort" columns is not supported. Using the first one only');
      }
      if (sortColumnsWithDefaultSort.length > 0) {
        const initialSortColumn: TableColumnHeader = sortColumnsWithDefaultSort[0];
        if (initialSortColumn) {
          initialSortColumn.headerSortState =
            initialSortColumn.defaultSort.toLowerCase() === 'asc' ? SortState.DEFAULT : SortState.REVERTED;
        }
      }
    });
  }

  public getFilters() {
    return this.filtersColumn.searchQuery;
  }

  private openFilters() {
    if (this.filtersColumn) {
      this.filtersColumn.open = true;
    }
  }

  private closeFilters() {
    if (this.filtersColumn) {
      this.filtersColumn.open = false;
    }
  }

  private toggleFilters() {
    if (this.filtersColumn) {
      this.filtersColumn.open = !this.filtersColumn.open;
    }
  }

  private setFilters(filters: SearchQuery) {
    this.filtersColumn.searchQuery = filters;
  }

  private sortKeyChanged(sortKey: string) {
    const existing: SortExpression = this.sortKey.find(k => k.key === sortKey);
    if (existing) {
      // Sort key didn't change, it only changed direction
      existing.direction = existing.direction === SortDirection.ASCENDING ?
        SortDirection.DESCENDING : SortDirection.ASCENDING;
    } else {
      // Sort key changed
      this.sortKey = [{key: sortKey, direction: SortDirection.ASCENDING}];
    }
    this.sortChange.emit(this.sortKey);

    this.headers.forEach((header: TableColumnHeader) => {
      if (header.sortKey !== sortKey) {
        header.headerSortState = SortState.INACTIVE;
      } else {
        header.headerSortState = this.sortKey[0].direction === SortDirection.ASCENDING ?
          SortState.DEFAULT : SortState.REVERTED;
      }
    });
  }
}

